import { useState } from 'react';

// components, keys, and utils
import {
    BrowseDataProductRelatedEntityType,
    DataDataProductTable,
    DomainEntityType,
} from './BrowseDataProductTableType';
import utilService from '../../utils/util.service';

// generated
import { useListDataProductsQuery } from '../../graphql/dataProduct.generated';

export interface IPagination {
    start: number;
    count: number;
}

interface IGridRequest {
    pagination: IPagination;
}

const { debounce } = utilService;

/**
 * Custom hook `useBrowseDataProductTableRequest` for managing and fetching data product table requests.
 *
 * @param {IGridRequest} params - The input parameters for the hook.
 * @param {Object} params.pagination - Pagination details for the request.
 * @param {number} params.pagination.start - The starting index for pagination.
 * @param {number} params.pagination.count - The number of items to fetch per page.
 *
 * @returns {Object} - The hook returns an object containing the following properties:
 *
 * @property {DataDataProductTable[]} dataSource - The transformed data source for the data product table.
 * @property {number} total - The total number of data products available.
 * @property {boolean} loading - Indicates whether the data is currently being loaded.
 * @property {Function} onSearch - A debounced function to handle search input changes.
 * @property {Function} onApplyFilter - A function to apply a filter based on type and value.
 * @property {Function} onResetFilter - A function to reset a specific filter by type.
 * @property {Function} refetch - A function to refetch the data manually.
 * @property {string} searchText - The current search text used for filtering.
 * @property {Array} filterOptions - The current filter options applied to the data.
 * @property {Function} fetchMore - A function to fetch more data for pagination.
 *
 * @description
 * This hook is used to fetch and manage data for a data product table. It supports
 * pagination, search, and filtering functionalities. The data is fetched using the
 * `useListDataProductsQuery` GraphQL query, and the results are transformed into a
 * format suitable for rendering in a table. The hook also provides utility functions
 * for handling search and filter operations.
 */
const useBrowseDataProductTableRequest = ({ pagination }: IGridRequest) => {
    const [filterOptions, setFilterOptions]: any = useState([]);
    const [searchText, setSearchText] = useState('');

    const { data, loading, refetch, fetchMore } = useListDataProductsQuery({
        variables: {
            input: {
                start: pagination.start,
                count: pagination.count,
                query: searchText,
                filters: filterOptions.map((filter) => ({
                    field: filter.field === 'dataProducts' ? 'urn' : filter.field,
                    values: filter.values,
                    condition: filter.condition,
                })),
            },
        },
        fetchPolicy: 'network-only',
    });

    const dataSource: DataDataProductTable[] =
        data?.listDataProducts?.dataProducts?.map((item) => {
            const dataProduct: BrowseDataProductRelatedEntityType = {
                urn: item?.urn || '',
                name: item.properties?.name || '',
                type: item?.type || '',
            };

            const domain: BrowseDataProductRelatedEntityType = {
                urn: item.domain?.domain?.urn || '',
                name: item.domain?.domain?.properties?.name || '',
                type: item.domain?.domain?.type || '',
                path:
                    item.domain?.domain?.parentDomains?.domains
                        ?.map((domainElem: DomainEntityType) => domainElem.properties?.name)
                        .filter((name) => name)
                        .reverse()
                        .join(' / ') || '',
                entity: item.domain?.domain,
            };

            const glossaryTerms: BrowseDataProductRelatedEntityType[] =
                item.glossaryTerms?.terms?.map((term) => ({
                    urn: term.term?.urn || '',
                    name: term.term?.properties?.name || '',
                    type: term.term?.type || '',
                    entity: term.term,
                })) || [];

            const tags: BrowseDataProductRelatedEntityType[] =
                item.tags?.tags?.map((tag) => ({
                    urn: tag.tag?.urn || '',
                    name: tag.tag?.name || '',
                    type: tag.tag?.type || '',
                    entity: tag.tag,
                })) || [];

            const ownerData: BrowseDataProductRelatedEntityType[] =
                item.ownership?.owners?.map((owner) => ({
                    urn: owner.owner?.urn || '',
                    name: owner.owner?.info?.displayName || '',
                    type: owner.owner?.type || '',
                    entity: owner.owner ?? null,
                    ownershipType: owner.ownershipType ?? null,
                    ownerType: owner.type || '',
                })) || [];

            return {
                id: item?.urn || '',
                dataProduct,
                domain,
                glossaryTerms,
                tags,
                assets: item.entities?.total || 0,
                owner: ownerData,
            };
        }) || [];

    /**
     * Debounced search handler for updating the search text state.
     *
     * @param e - The change event from the search input field.
     *
     * This function is triggered when the user types in the search input field.
     * It debounces the input to avoid frequent state updates and sets the `searchText`
     * state with the lowercased value of the input after a delay of 500ms.
     * */
    const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = e.target.value.toLowerCase();
        setSearchText(searchValue);
    }, 500);

    /**
     * Updates the filter options based on the selected filter type and value.
     *
     * @param type - The type of the filter to be applied (e.g., "tags", "domain").
     * @param urn - The unique identifier (URN) of the filter value to be applied.
     *
     * This function checks if a filter of the specified type already exists in the current filter options.
     * - If the filter exists, it updates the filter's values with the provided URN.
     * - If the filter does not exist, it adds a new filter with the specified type and value.
     *
     * The updated filter options are then set in the component's state.
     */
    const onApplyFilter = (type, urn) => {
        const filterExists = filterOptions.some((filter) => filter.field === type);
        // Update the filter options based on the selected filter type and value
        const updatedFilters = filterExists
            ? filterOptions.map((filter) => (filter.field === type ? { ...filter, values: [urn] } : filter))
            : [...filterOptions, { field: type, values: [urn] }];
        setFilterOptions(updatedFilters);
    };

    const onResetFilter = (type: string) => {
        const updatedFilters = filterOptions.filter((filter) => filter.field !== type);
        setFilterOptions(updatedFilters);
    };

    return {
        dataSource,
        total: data?.listDataProducts?.total || 0,
        loading,
        onSearch,
        onApplyFilter,
        onResetFilter,
        refetch,
        searchText,
        filterOptions,
        fetchMore,
    };
};

export default useBrowseDataProductTableRequest;
